// frontend/src/site/models/corretor/SiteHomeCorretor.jsx
import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import SiteHero from '../../components/SiteHero';
import SiteFilterBar from '../../components/SiteFilterBar';
import SiteListingGrid from '../../components/SiteListingGrid';

import { useSite } from '../../SiteContext';
import useSiteCatalog from '../../hooks/useSiteCatalog';

import {
  containerClasses,
  sectionClasses,
  headingClasses,
  buttonClasses,
  mutedTextClasses,
} from '../../theme/siteStyles';

const cx = (...c) => c.filter(Boolean).join(' ');

function isOn(estrutura, id) {
  const arr = Array.isArray(estrutura) ? estrutura : [];
  const it = arr.find((x) => String(x?.id || '').trim() === id);
  if (!it) return true;
  return it.ativo !== false;
}

function safeStr(v) {
  return String(v || '').toLowerCase();
}

function isTerreno(item) {
  const tipo = safeStr(item?.tipo || item?.categoria || item?.tipoImovel);
  return tipo.includes('terreno') || tipo.includes('lote');
}

export default function SiteHomeCorretor() {
  const { handle } = useParams();

  const { corretorId, ownerWhatsUrl, theme, estrutura, conteudo, perfilSite } =
    useSite();

  const hero = conteudo?.hero || {};
  const blocoImoveis = conteudo?.imoveis || {};
  const blocoTerrenos = conteudo?.terrenos || {};
  const ctaFinal = conteudo?.ctaFinal || {};

  // ✅ limite padrão agora é 6 (e trava em 6)
  const qtdImoveis = useMemo(() => {
    const n = Number(blocoImoveis?.quantidade);
    const safe = Number.isFinite(n) && n > 0 ? n : 6;
    return Math.min(safe, 6);
  }, [blocoImoveis?.quantidade]);

  const qtdTerrenos = useMemo(() => {
    const n = Number(blocoTerrenos?.quantidade);
    const safe = Number.isFinite(n) && n > 0 ? n : 6;
    return Math.min(safe, 6);
  }, [blocoTerrenos?.quantidade]);

  // ✅ 1) destaques do catálogo (destaque:true) – até 6
  const {
    docs: destaqueDocsRaw,
    load: loadDestaques,
    loading: loadingDestaques,
  } = useSiteCatalog(corretorId, { publicado: true, destaque: true }, 6);

  // ✅ 2) recentes do catálogo (publicado:true) – pega mais pra filtrar casa/terreno
  const {
    docs: recentesDocsRaw,
    load: loadRecentes,
    loading: loadingRecentes,
  } = useSiteCatalog(corretorId, { publicado: true }, 24);

  useEffect(() => {
    if (!corretorId) return;
    loadDestaques();
    loadRecentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corretorId]);

  // ✅ decide o que entra em “Imóveis em destaque” e “Terrenos”
  const { imoveisParaMostrar, terrenosParaMostrar } = useMemo(() => {
    const destaques = Array.isArray(destaqueDocsRaw) ? destaqueDocsRaw : [];
    const recentes = Array.isArray(recentesDocsRaw) ? recentesDocsRaw : [];

    // Terrenos: sempre últimos 6 terrenos (dos recentes)
    const terrenos = recentes.filter(isTerreno).slice(0, qtdTerrenos);

    // Destaques: somente NÃO-terrenos
    const destaquesImoveis = destaques
      .filter((x) => !isTerreno(x))
      .slice(0, qtdImoveis);

    // Fallback: se não tiver destaque marcado, pega últimas casas (não-terrenos) dos recentes
    const recentesImoveis = recentes
      .filter((x) => !isTerreno(x))
      .slice(0, qtdImoveis);

    return {
      imoveisParaMostrar: destaquesImoveis.length
        ? destaquesImoveis
        : recentesImoveis,
      terrenosParaMostrar: terrenos,
    };
  }, [destaqueDocsRaw, recentesDocsRaw, qtdImoveis, qtdTerrenos]);

  const loadingGrid = loadingDestaques || loadingRecentes;

  // textos
  const tituloDestaque = blocoImoveis?.titulo || 'Imóveis em Destaque';
  const subtituloDestaque =
    blocoImoveis?.subtitulo || 'Seleção do corretor para você.';

  const tituloTerrenos = blocoTerrenos?.titulo || 'Terrenos';
  const subtituloTerrenos =
    blocoTerrenos?.subtitulo || 'Lotes e oportunidades disponíveis.';

  const mostrarVerTodosImoveis = blocoImoveis?.mostrarBotaoVerTodos !== false;
  const textoVerTodosImoveis =
    blocoImoveis?.textoBotaoVerTodos || 'Ver mais imóveis';

  const mostrarVerTodosTerrenos = blocoTerrenos?.mostrarBotaoVerTodos !== false;
  const textoVerTodosTerrenos =
    blocoTerrenos?.textoBotaoVerTodos || 'Ver mais terrenos';

  // CTA final
  const ctaFinalTitle = ctaFinal?.titulo || 'Pronto para dar o próximo passo?';
  const ctaFinalDesc =
    ctaFinal?.descricao ||
    'Fale comigo e receba um atendimento direto e estratégico.';
  const ctaFinalBtn = ctaFinal?.textoBotao || 'Falar no WhatsApp';

  const ctaTipo = String(ctaFinal?.tipoAcao || 'whatsapp');
  const ctaLink = String(ctaFinal?.link || '').trim();
  const ctaHref = ctaTipo === 'link' && ctaLink ? ctaLink : ownerWhatsUrl || '';

  return (
    <div className="min-h-screen bg-[color:var(--bg)] text-[color:var(--text)]">
      {isOn(estrutura, 'hero') && (
        <SiteHero
          titulo={hero?.titulo}
          subtitulo={hero?.subtitulo}
          frase={hero?.frase}
          ctaPrimario={hero?.ctaPrimario}
          ctaSecundario={hero?.ctaSecundario}
          mostrarCtaSecundario={hero?.mostrarCtaSecundario}
          nome={perfilSite?.nome || ''}
          fotoUrl={perfilSite?.fotoPerfilUrl || ''}
          capaUrl={perfilSite?.siteCoverUrl || ''}
          whatsappUrl={ownerWhatsUrl || ''}
        >
          <SiteFilterBar />
        </SiteHero>
      )}

      {/* ✅ IMÓVEIS (destaque -> fallback últimas casas) */}
      {isOn(estrutura, 'imoveis') && (
        <section
          id="imoveis"
          className={cx(
            containerClasses(theme),
            sectionClasses(theme),
            '-mt-6 md:-mt-10'
          )}
        >
          <h2 className={cx(headingClasses(theme, 'h3'), 'text-center')}>
            {tituloDestaque}
          </h2>

          {subtituloDestaque ? (
            <p className={cx(mutedTextClasses(), 'text-center mt-2')}>
              {subtituloDestaque}
            </p>
          ) : null}

          <div className="mt-8">
            <SiteListingGrid
              items={imoveisParaMostrar}
              loading={loadingGrid}
              exhausted={true}
              whatsappUrl={ownerWhatsUrl || ''}
              cols={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            />
          </div>

          {mostrarVerTodosImoveis && (
            <div className="text-center mt-10">
              <Link
                to={`/u/${handle}/imoveis`}
                className={cx(buttonClasses(theme, 'secondary'), 'inline-flex')}
              >
                {textoVerTodosImoveis}
              </Link>
            </div>
          )}
        </section>
      )}

      {/* ✅ TERRENOS (sempre últimos 6 terrenos) */}
      {isOn(estrutura, 'terrenos') && (
        <section
          className={cx(containerClasses(theme), sectionClasses(theme))}
          id="terrenos"
        >
          <h2 className={cx(headingClasses(theme, 'h3'), 'text-center')}>
            {tituloTerrenos}
          </h2>

          {subtituloTerrenos ? (
            <p className={cx(mutedTextClasses(), 'text-center mt-2')}>
              {subtituloTerrenos}
            </p>
          ) : null}

          <div className="mt-8">
            <SiteListingGrid
              items={terrenosParaMostrar}
              loading={loadingGrid}
              exhausted={true}
              whatsappUrl={ownerWhatsUrl || ''}
              cols={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            />
          </div>

          {mostrarVerTodosTerrenos && (
            <div className="text-center mt-10">
              <Link
                to={`/u/${handle}/imoveis?tipo=terreno`}
                className={cx(buttonClasses(theme, 'secondary'), 'inline-flex')}
              >
                {textoVerTodosTerrenos}
              </Link>
            </div>
          )}
        </section>
      )}

      {isOn(estrutura, 'ctaFinal') && (
        <section className={cx(containerClasses(theme), sectionClasses(theme))}>
          <div className="rounded-2xl border bg-white/60 dark:bg-white/5 p-6 md:p-10 text-center">
            <h3 className={cx(headingClasses(theme, 'h3'))}>{ctaFinalTitle}</h3>

            {ctaFinalDesc ? (
              <p className={cx(mutedTextClasses(), 'mt-3 max-w-2xl mx-auto')}>
                {ctaFinalDesc}
              </p>
            ) : null}

            <div className="mt-6 flex justify-center">
              {ctaHref ? (
                <a
                  href={ctaHref}
                  target="_blank"
                  rel="noreferrer"
                  className={cx(buttonClasses(theme, 'primary'), 'px-7')}
                >
                  {ctaFinalBtn}
                </a>
              ) : (
                <button
                  type="button"
                  className={cx(
                    buttonClasses(theme, 'primary'),
                    'px-7 opacity-60 cursor-not-allowed'
                  )}
                  disabled
                  title="WhatsApp não configurado"
                >
                  {ctaFinalBtn}
                </button>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
